import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { customerService } from '../../services/firebase'
import { qrService } from '../../services/qrService'
import { QrCode, Phone, Lightbulb } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import BottomNav from '../../components/BottomNav'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export default function CustomerQRCode() {
  const { currentUser } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [qrCodeDataURL, setQrCodeDataURL] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchCustomerData() {
      if (!currentUser) return

      try {
        await customerService.ensureCustomerProfileForUser(currentUser)
        const customerData = await customerService.getCustomerByUserId(currentUser.uid)
        if (customerData?.id) {
          const ensured = await customerService.ensureCustomerCode(customerData.id)
          customerData.customerCode = customerData.customerCode || ensured
        }
        setCustomer(customerData)

        if (customerData) {
          const qrData = qrService.generateCustomerQRData(
            customerData.id,
            customerData.phone,
            customerData.customerCode || null
          )
          const qrDataURL = await qrService.generateQRCode(qrData, {
            width: 300,
            logoSrc: '/brand/trendsetter-logo.png',
            logoSize: 52,
          })
          setQrCodeDataURL(qrDataURL)
        }
      } catch (error) {
        console.error('Error fetching customer data:', error)
        toast.error('Failed to load QR code')
      } finally {
        setLoading(false)
      }
    }

    fetchCustomerData()
  }, [currentUser])

  // QR is stable per customer (no regenerate/download needed).

  if (loading) {
    return (
      <div className="flex min-h-96 items-center justify-center bg-background">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!customer || !qrCodeDataURL) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center px-4 text-center">
        <QrCode className="mb-4 size-14 text-muted-foreground" />
        <h2 className="text-lg font-semibold text-foreground">QR code unavailable</h2>
        <p className="mt-2 text-sm text-muted-foreground">Please contact support.</p>
      </div>
    )
  }

  const steps = [
    { step: 1, title: 'Arrive', description: 'Pull into the wash bay.' },
    { step: 2, title: 'Open this page', description: 'Show staff this QR — they scan your phone.' },
    { step: 3, title: 'Confirm wash', description: 'Agree package and vehicle class with staff.' },
    { step: 4, title: 'Collect', description: 'Wait for ready — pay at the cashier if needed.' },
  ]

  return (
    <>
      <div className="min-h-screen bg-background pb-32">
        <div className="mx-auto max-w-lg space-y-6 px-4 py-8">
          <div className="space-y-1 border-b border-border pb-6 text-center">
            <Badge variant="outline" className="mb-2 border-primary/30 text-primary">
              Show this to staff
            </Badge>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">Your check-in QR</h1>
            <p className="text-sm text-muted-foreground">
              Hold this screen up at the wash bay. Staff scan it — you do not need to scan anything.
            </p>
          </div>

          <Card className="overflow-hidden border-border/80 shadow-sm">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <QrCode className="size-7" />
              </div>
              <CardTitle className="text-lg">{customer.name}</CardTitle>
              <CardDescription className="flex items-center justify-center gap-1.5">
                <Phone className="size-3.5" />
                {customer.phone}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {customer.customerCode && (
                <div className="text-center">
                  <p className="text-xs text-muted-foreground">Customer code (if staff ask)</p>
                  <p className="mt-1 font-mono text-sm font-semibold tracking-widest text-foreground">
                    {customer.customerCode}
                  </p>
                </div>
              )}
              <div className="rounded-xl border border-primary/25 bg-primary/5 px-3 py-2 text-center text-sm font-medium text-foreground">
                Brighten your screen · keep this QR on screen
              </div>
              <div className="flex justify-center rounded-xl border border-border bg-white p-6">
                <img src={qrCodeDataURL} alt="Customer QR" className="size-64 max-w-full" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">How it works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {steps.map((item) => (
                <div key={item.step} className="flex gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary">
                    {item.step}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-primary/5 shadow-none">
            <CardContent className="flex gap-3 pt-6">
              <Lightbulb className="size-5 shrink-0 text-primary" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Tips</p>
                <ul className="mt-2 list-inside list-disc space-y-1">
                  <li>You never scan with your own camera — staff do.</li>
                  <li>Turn brightness up so the code reads easily.</li>
                  <li>Save a screenshot if signal is poor.</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <BottomNav />
    </>
  )
}
